"use client";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { headerData } from "./Navigation/menuData";
import Logo from "./Logo";
import HeaderLink from "./Navigation/HeaderLink";
import MobileHeaderLink from "./Navigation/MobileHeaderLink";
import { Icon } from "@iconify/react";

const Header: React.FC = () => {
  const [sticky, setSticky] = useState(false);
  const [navbarOpen, setNavbarOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  const mobileMenuRef = useRef<HTMLDivElement>(null);

  // Wait until mounted on client to avoid hydration mismatch
  useEffect(() => {
    setMounted(true);
  }, []);

  // Handle Sticky Scroll
  useEffect(() => {
    if (!mounted) return;
    const handleScroll = () => {
      setSticky(window.scrollY >= 40);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [mounted]);

  // Handle Click Outside Mobile Menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target as Node) &&
        navbarOpen
      ) {
        setNavbarOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [navbarOpen]);

  // Lock Body Scroll when Menu is Open
  useEffect(() => {
    document.body.style.overflow = navbarOpen ? "hidden" : "";
  }, [navbarOpen]);

  // Use consistent classes during SSR - default to non-sticky state
  const headerClasses =
    mounted && sticky
      ? "bg-white/80 dark:bg-darkheader/90 backdrop-blur-md shadow-lg py-2"
      : "bg-transparent py-4";

  return (
    <>
      <header
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${headerClasses}`}
      >
        <div className="container mx-auto lg:max-w-(--breakpoint-xl) md:max-w-(--breakpoint-md) flex justify-between items-center px-4">
          <div className="flex justify-start items-center gap-10">
            {/* LOGO */}
            <div className="relative z-50">
              <Logo />
            </div>

            {/* DESKTOP NAV */}
            <nav className="hidden lg:flex items-center gap-8">
              {headerData.map((item, index) => (
                <HeaderLink key={index} item={item} />
              ))}
            </nav>
          </div>

          {/* UTILITIES (Theme Toggle / Contact / Mobile Button) */}
          <div className="flex items-center gap-4">
            {/* <button
            aria-label="Toggle theme"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            className="flex h-8 w-8 items-center justify-center text-body-color duration-300 dark:text-white"
          >
            <svg
              viewBox="0 0 16 16"
              className={`hidden h-6 w-6 dark:block ${
                !sticky && pathUrl === "/" && "text-white"
              }`}
            >
              <path
                d="M4.50663 3.2267L3.30663 2.03337L2.36663 2.97337L3.55996 4.1667L4.50663 3.2267ZM2.66663 7.00003H0.666626V8.33337H2.66663V7.00003ZM8.66663 0.366699H7.33329V2.33337H8.66663V0.366699V0.366699ZM13.6333 2.97337L12.6933 2.03337L11.5 3.2267L12.44 4.1667L13.6333 2.97337ZM11.4933 12.1067L12.6866 13.3067L13.6266 12.3667L12.4266 11.1734L11.4933 12.1067ZM13.3333 7.00003V8.33337H15.3333V7.00003H13.3333ZM7.99996 3.6667C5.79329 3.6667 3.99996 5.46003 3.99996 7.6667C3.99996 9.87337 5.79329 11.6667 7.99996 11.6667C10.2066 11.6667 12 9.87337 12 7.6667C12 5.46003 10.2066 3.6667 7.99996 3.6667ZM7.33329 14.9667H8.66663V13H7.33329V14.9667ZM2.36663 12.36L3.30663 13.3L4.49996 12.1L3.55996 11.16L2.36663 12.36Z"
                fill="#FFFFFF"
              />
            </svg>
            <svg
              viewBox="0 0 23 23"
              className={`h-8 w-8 text-dark dark:hidden ${
                !sticky && pathUrl === "/" && "text-white"
              }`}
            >
              <path d="M16.6111 15.855C17.591 15.1394 18.3151 14.1979 18.7723 13.1623C16.4824 13.4065 14.1342 12.4631 12.6795 10.4711C11.2248 8.47905 11.0409 5.95516 11.9705 3.84818C10.8449 3.9685 9.72768 4.37162 8.74781 5.08719C5.7759 7.25747 5.12529 11.4308 7.29558 14.4028C9.46586 17.3747 13.6392 18.0253 16.6111 15.855Z" />
            </svg>
          </button>     */}

            {/* Mobile Toggle Button */}
            {/* Mobile Toggle Button */}
            <button
              onClick={() => setNavbarOpen(!navbarOpen)}
              className={`lg:hidden p-2 rounded-md text-midnight_text dark:text-white hover:bg-gray-100 dark:hover:bg-white/10 transition-all ${
                navbarOpen
                  ? "opacity-0 invisible pointer-events-none"
                  : "opacity-100 visible"
              }`}
              aria-label="Toggle mobile menu"
            >
              <Icon icon="ion:menu" className="text-2xl" />
            </button>
          </div>
        </div>
      </header>

      {/* MOBILE MENU OVERLAY & DRAWER */}
      {/* Increased z-index to 999 to sit above the fixed header (z-50) */}
      <div
        className={`fixed inset-0 z-[999] lg:hidden transition-all duration-300 ${
          navbarOpen
            ? "opacity-100 visible"
            : "opacity-0 invisible pointer-events-none"
        }`}
      >
        {/* Backdrop - Added onClick to ensure clicking outside closes the menu */}
        <div
          className="absolute inset-0 bg-black/40 "
          onClick={() => setNavbarOpen(false)}
        />

        {/* Drawer */}
        <div
          ref={mobileMenuRef}
          className={`absolute top-0 right-0 h-full w-[300px] bg-white dark:bg-darkheader shadow-2xl transform transition-transform duration-300 ease-out ${
            navbarOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="flex flex-col h-full">
            {/* Drawer Header */}
            <div className="flex items-center justify-between p-5 border-b border-gray-100 dark:border-white/10">
              <span className="text-lg font-medium text-midnight_text dark:text-white">
                Menu
              </span>
              <button
                onClick={() => setNavbarOpen(false)}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                aria-label="Close menu"
              >
                <Icon icon="ion:close" className="text-xl dark:text-white" />
              </button>
            </div>

            {/* Drawer Links */}
            <div className="flex-1 overflow-y-auto py-2">
              <nav className="flex flex-col">
                {headerData.map((item, index) => (
                  <MobileHeaderLink
                    key={index}
                    item={item}
                    closeMenu={() => setNavbarOpen(false)}
                  />
                ))}
              </nav>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
